import React, { useState } from "react";
import {
  PlusIcon, EyeIcon, PencilAltIcon, TrashIcon, SearchIcon,
  CheckCircleIcon,
} from "@heroicons/react/solid";
import Cookies from "universal-cookie";
import { Button, DateInput } from "../../components/ui";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import { formatDateWithHyphens } from "../../utility";
import RoundedContainer from "../../components/containers/RoundedContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";
import DeleteConfirmationModal from "../../components/ui/DeleteConfirmationModal";
import Pagination from "../../components/pagination";
import InlineLoader from "../../components/loader/InlineLoader";
import { ROLE } from "../../config";
import { usePettyCashFundRequests } from "../../hooks/petty_cash/usePettyCashFundRequests";
import { usePettyCashSettings } from "../../hooks/petty_cash/usePettyCashSettings";
import FundRequestForm from "../../components/petty_cash/FundRequestForm";
import FundRequestDetailModal from "../../components/petty_cash/FundRequestDetailModal";
import FundRequestApprovalModal from "../../components/petty_cash/FundRequestApprovalModal";

const cookies = new Cookies();

const statusStyles = {
  pending: "status-pending",
  approved: "status-approved",
  rejected: "status-rejected",
  partial_approval: "bg-yellow-100 text-yellow-800 px-2.5 py-1 rounded-full text-xs font-bold",
};

const statusLabels = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  partial_approval: "Partial Approval",
};

function PettyCashFundRequests() {
  const role = cookies.get(ROLE);
  const isAdmin = role === "COMPANY_ADMIN";

  const {
    fundRequestList,
    filters,
    searchQuery,
    selectedRequestId,
    setSelectedRequestId,
    singleRequest,
    isCreating,
    isUpdating,
    isDeleting,
    isApproving,
    handleSearchChange,
    handleFilterChange,
    handlePageClick,
    handleViewDetails,
    createRequest,
    updateRequest,
    deleteRequest,
    approveRequest,
  } = usePettyCashFundRequests();

  const { settingsData } = usePettyCashSettings();
  const settings = settingsData.data?.data;
  const priorNoDays = settings && !Array.isArray(settings) ? parseInt(settings.prior_no_days, 10) : 0;
  const isSettingsLoading = settingsData.isLoading;

  const [formModal, setFormModal] = useState({ open: false, edit: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [approvalModal, setApprovalModal] = useState({ open: false, request: null });

  const openCreate = () => setFormModal({ open: true, edit: null });
  const openEdit = (req) => setFormModal({ open: true, edit: req });
  const closeForm = () => setFormModal({ open: false, edit: null });

  const openApproval = (req) => setApprovalModal({ open: true, request: req });
  const closeApproval = () => setApprovalModal({ open: false, request: null });

  const handleFormSubmit = (payload) => {
    if (formModal.edit) {
      updateRequest(payload);
    } else {
      createRequest(payload);
    }
    closeForm();
  };

  const handleApprove = (payload) => {
    approveRequest(payload);
    closeApproval();
  };

  const requests = fundRequestList.data?.data || [];
  const totalCount = fundRequestList.data?.total_count || 0;
  const activeRequest = singleRequest.data?.data;

  return (
    <div className="py-6 px-4 md:px-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <BreadCrumbs
          crumbs={[
            { label: "Dashboard", link: "/" },
            { label: "Fund Requests", link: "" },
          ]}
        />
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Fund Requests</h1>
            <p className="text-gray-500 mt-1">
              {isAdmin
                ? "Review and approve employee fund requests."
                : "Submit and track your fund requests."}
            </p>
          </div>
          {!isAdmin && (
            <Button onClick={openCreate} className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700">
              <PlusIcon className="w-5 h-5" />
              <span>New Request</span>
            </Button>
          )}
        </div>

        <RoundedContainer className="p-5 shadow-sm border border-gray-150 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Search</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by name or remarks..."
                  className="pl-10 pr-4 py-2 w-full rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm shadow-sm"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Status</label>
              <select
                value={filters.approval_status}
                onChange={(e) => handleFilterChange("approval_status", e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="partial_approval">Partial Approval</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">From Date</label>
              <DateInput
                label={null}
                value={filters.from_date}
                onChange={(date) => handleFilterChange("from_date", date ? formatDateWithHyphens(date) : "")}
                placeholderText="Select Date"
                isClearable
                selectsStart
                startDate={filters.from_date ? new Date(filters.from_date) : null}
                endDate={filters.to_date ? new Date(filters.to_date) : null}
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">To Date</label>
              <DateInput
                label={null}
                value={filters.to_date}
                onChange={(date) => handleFilterChange("to_date", date ? formatDateWithHyphens(date) : "")}
                placeholderText="Select Date"
                isClearable
                selectsEnd
                startDate={filters.from_date ? new Date(filters.from_date) : null}
                endDate={filters.to_date ? new Date(filters.to_date) : null}
                minDate={filters.from_date ? new Date(filters.from_date) : null}
              />
            </div>
          </div>
        </RoundedContainer>

        {fundRequestList.isLoading ? (
          <div className="flex items-center justify-center py-12 w-full">
            <InlineLoader className="w-8 h-8 text-primary" />
          </div>
        ) : (
          <>
            <RoundedContainer className="hidden md:block border border-gray-150 shadow-sm p-4">
              <div className="max-w-full overflow-x-auto">
                <Table className="table-auto w-full">
                  <TableHead>
                    <TableHeaderCell>#</TableHeaderCell>
                    <TableHeaderCell>Employee</TableHeaderCell>
                    <TableHeaderCell>Period</TableHeaderCell>
                    <TableHeaderCell>Requested</TableHeaderCell>
                    <TableHeaderCell>Approved</TableHeaderCell>
                    <TableHeaderCell className="text-center">Status</TableHeaderCell>
                    <TableHeaderCell className="text-center">Actions</TableHeaderCell>
                  </TableHead>
                  <TableBody>
                    {requests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-gray-400">
                          No fund requests found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      requests.map((req, idx) => (
                        <TableRow key={req.request_id}>
                          <TableCell className="text-gray-500">{filters.page_number * filters.limit + idx + 1}</TableCell>
                          <TableCell className="font-semibold text-gray-800">{req.employee_name || "-"}</TableCell>
                          <TableCell className="text-gray-600 text-sm">
                            {req.from_date} - {req.to_date}
                          </TableCell>
                          <TableCell className="font-semibold text-gray-800">₹{req.requested_amount}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {req.approved_amount > 0 ? `₹${req.approved_amount}` : "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={statusStyles[req.approval_status] || "status-pending"}>
                              {statusLabels[req.approval_status] || req.approval_status}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => handleViewDetails(req.request_id)}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                title="View Details"
                              >
                                <EyeIcon className="w-5 h-5" />
                              </button>
                              {req.approval_status === "pending" && isAdmin && (
                                <button
                                  onClick={() => openApproval(req)}
                                  className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                                  title="Approve/Reject"
                                >
                                  <CheckCircleIcon className="w-5 h-5" />
                                </button>
                              )}
                              {req.approval_status === "pending" && !isAdmin && (
                                <>
                                  <button
                                    onClick={() => openEdit(req)}
                                    className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                                    title="Edit"
                                  >
                                    <PencilAltIcon className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteModal({ open: true, id: req.request_id })}
                                    className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                                    title="Delete"
                                  >
                                    <TrashIcon className="w-5 h-5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </RoundedContainer>

            <div className="md:hidden space-y-4">
              {requests.length === 0 ? (
                <div className="text-center py-8 text-gray-400 bg-white rounded-2xl border border-gray-150">
                  No fund requests found.
                </div>
              ) : (
                requests.map((req) => (
                  <div key={req.request_id} className="bg-white rounded-2xl border border-gray-150 p-5 shadow-sm flex flex-col space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-gray-800 font-bold block">{req.employee_name || "-"}</span>
                        <span className="text-xs text-gray-500">{req.employee_code || "-"}</span>
                      </div>
                      <span className={statusStyles[req.approval_status] || "status-pending"}>
                        {statusLabels[req.approval_status] || req.approval_status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-400 block text-xs">Period</span>
                        <span className="font-semibold">{req.from_date} - {req.to_date}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block text-xs">Requested</span>
                        <span className="font-bold text-gray-900">₹{req.requested_amount}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(req.request_id)}
                          className="p-2 rounded-xl text-gray-500 bg-gray-50 border border-gray-200 active:scale-95 transition-all"
                          title="View Details"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        {req.approval_status === "pending" && isAdmin && (
                          <button
                            onClick={() => openApproval(req)}
                            className="p-2 rounded-xl text-green-500 bg-green-50 border border-green-100 active:scale-95 transition-all"
                            title="Approve/Reject"
                          >
                            <CheckCircleIcon className="w-5 h-5" />
                          </button>
                        )}
                        {req.approval_status === "pending" && !isAdmin && (
                          <>
                            <button
                              onClick={() => openEdit(req)}
                              className="p-2 rounded-xl text-blue-600 bg-blue-50 border border-blue-100 active:scale-95 transition-all"
                              title="Edit"
                            >
                              <PencilAltIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => setDeleteModal({ open: true, id: req.request_id })}
                              className="p-2 rounded-xl text-red-500 bg-red-50 border border-red-100 active:scale-95 transition-all"
                              title="Delete"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <RoundedContainer className="border border-gray-150 shadow-sm p-4">
              <Pagination
                currentPage={filters.page_number}
                lengthofItems={totalCount}
                limit={filters.limit}
                onPageChange={handlePageClick}
              />
            </RoundedContainer>
          </>
        )}
      </div>

      <FundRequestForm
        open={formModal.open}
        onClose={closeForm}
        editRequest={formModal.edit}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isSettingsLoading={isSettingsLoading}
        priorNoDays={priorNoDays}
        onSubmit={handleFormSubmit}
      />

      <FundRequestDetailModal
        open={!!selectedRequestId}
        onClose={() => setSelectedRequestId(null)}
        request={activeRequest}
        isLoading={singleRequest.isLoading}
      />

      <FundRequestApprovalModal
        open={approvalModal.open}
        onClose={closeApproval}
        request={approvalModal.request}
        isApproving={isApproving}
        onApprove={handleApprove}
      />

      <DeleteConfirmationModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onDelete={() => {
          deleteRequest({ request_id: deleteModal.id });
          setDeleteModal({ open: false, id: null });
        }}
        isLoading={isDeleting}
        message="Are you sure you want to delete this fund request?"
      />
    </div>
  );
}

export default PettyCashFundRequests;
